import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { UserContext } from './userContext';
import './App.css';
import logo from './logo.jpg';
import pessoa from './pessoa.png';
import banner1 from './pages/1.png';
import banner2 from './pages/2.png';
import banner3 from './pages/3.png';
import banner4 from './pages/4.png';
import banner5 from './pages/5.png';
import banner7 from './pages/7.png'
import {
  FaHome, FaQuestionCircle, FaInfoCircle,
  FaLayerGroup, FaLaptopCode, FaCogs, FaBriefcase, FaGraduationCap,
  FaClipboardCheck, FaSignInAlt, FaGift, FaStar, FaSearch, FaTimes,
  FaHeart, FaRegHeart, FaBars,
  // Ícones para subcategorias
  FaCode, FaCalculator, FaDatabase, FaGlobe, FaServer, FaBook,
  FaFlask, FaUsers, FaTools, FaChartBar, FaBrain, FaRobot,
  FaPencilAlt, FaAtom, FaLanguage, FaIndustry,
  FaClipboardList, FaMicroscope, FaUserGraduate, FaUserShield
} from 'react-icons/fa';
import {
  collection, getDocs, getDoc, doc,
  query, where, limit, setDoc, updateDoc, arrayUnion, arrayRemove,
  enableNetwork, disableNetwork
} from 'firebase/firestore';
import { db, auth } from './firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';

// Importações dos componentes existentes
import PremiumPlans from './pages/PremiumPlans';
import Login from './pages/Login';
import Cadastro from './pages/Cadastro';
import QuemSomos from './pages/QuemSomos';
import PerguntasFrequentes from './pages/PerguntasFrequentes';
import UserMenu from './pages/UserMenu';
import Perfil from './pages/Perfil';
import AdminLogin from './pages/AdminLogin';
import AdminPanel from './pages/AdminPanel';
import GerenciarAdministradores from './pages/GerenciarAdministradores';
import GerenciarCategorias from './pages/GerenciarCategorias';
import GerenciarMateriais from './pages/GerenciarMateriais';
import VerConteudo from './pages/VerConteudo';
import GerenciarUsuarios from './pages/GerenciarUsuarios';
import GerenciarPlanos from './pages/GerenciarPlanos';
import MateriaisPorCategoria from './pages/MateriaisPorCategoria';
import MateriaisPorSubcategoria from './pages/MateriaisPorSubcategoria';
import MateriaisAprovacao from './pages/MateriaisAprovacao';
import ResultadosBusca from './pages/ResultadosBusca';
import Depoimentos from './components/Depoimentos';
import CategoriaDetalhada from './pages/CategoriaDetalhada';
import MeusFavoritos from './pages/MeusFavoritos';

// Novos componentes - Sistema de Avisos/Notificações
import PerguntaAluno from './pages/PerguntaAluno';
import PerguntasAdmin from './pages/PerguntasAdmin';
import BotaoNaoEncontrou from './components/BotaoNaoEncontrou';
import GerenciarAvisos from './pages/GerenciarAvisos';
import PopupAvisos from './components/PopupAvisos';
import { normalizeText } from './utils/text';
import { getEffectivePlan } from './utils/effectivePlan';

function App() {
  const navigate = useNavigate();
  const location = useLocation();

  const [user, setUser] = useState(undefined); // undefined = carregando; null = deslogado; objeto = logado
  const [isAdminOrSpecialist, setIsAdminOrSpecialist] = useState(undefined);

  const [activeCategory, setActiveCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [mainSearchTerm, setMainSearchTerm] = useState('');
  const [categories, setCategories] = useState({});

  // Favoritas do usuário
  const [userFavorites, setUserFavorites] = useState([]);

  // Estados para responsividade
  const [isMobile, setIsMobile] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const hasAdminAccess = isAdminOrSpecialist || user?.role === 'admin' || user?.role === 'especialista';

  const RequireAdminOrSpecialist = ({ children }) => {
    if (user === undefined || isAdminOrSpecialist === undefined) {
      return (
        <div className="gerenciar-usuarios admin-panel-container">
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
            flexDirection: 'column',
            gap: '1rem'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '4px solid #e2e8f0',
              borderTopColor: '#209cd3',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            <p>Carregando...</p>
          </div>
        </div>
      );
    }

    if (!hasAdminAccess) {
      return <Navigate to="/admin-login" replace />;
    }

    return children;
  };

  // =========================
  // Otimização global do Firestore
  // =========================
  useEffect(() => {
    let active = true;
    let switching = false;
    let networkEnabled = true;
    let desiredEnabled = true;

    const applyNetworkState = async () => {
      if (!active || switching || desiredEnabled === networkEnabled) return;
      switching = true;
      try {
        if (desiredEnabled) {
          await enableNetwork(db);
        } else {
          await disableNetwork(db);
        }
        networkEnabled = desiredEnabled;
      } catch (_) {
        // Evita quebrar a UI caso a troca de estado falhe.
      } finally {
        switching = false;
        // Se o estado desejado mudou durante a troca, reaplica.
        if (active && desiredEnabled !== networkEnabled) {
          applyNetworkState();
        }
      }
    };

    const handleVisibility = () => {
      desiredEnabled = document.visibilityState === 'visible';
      applyNetworkState();
    };

    handleVisibility();
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      active = false;
      document.removeEventListener('visibilitychange', handleVisibility);
      enableNetwork(db).catch(() => {});
    };
  }, []);

  // =========================
  // Verificação de responsividade
  // =========================
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setSidebarOpen(false);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fechar sidebar quando navegar em mobile
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [location.pathname, isMobile]);

  // 🚀 Garantir MODAL por cima no mobile e tirar o sidebar da frente
  useEffect(() => {
    // Quando existir uma categoria ativa (modal aberta) no mobile:
    if (isMobile) {
      if (activeCategory) {
        // Fecha/oculta o sidebar para não cobrir a modal
        setSidebarOpen(false);
        // Marca o <body> para o CSS saber que há modal aberta
        document.body.classList.add('has-modal');
      } else {
        document.body.classList.remove('has-modal');
      }
    } else {
      // Em desktop, nunca deixamos essa classe
      document.body.classList.remove('has-modal');
    }

    // Cleanup se o componente desmontar
    return () => document.body.classList.remove('has-modal');
  }, [activeCategory, isMobile, setSidebarOpen]);


  // =========================
  // 1) Persistência do Login
  // =========================
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (fbUser) => {
      try {
        if (!fbUser) {
          setUser(null);
          setIsAdminOrSpecialist(false);
          setUserFavorites([]);
          return;
        }

        // Buscar dados do usuário - primeiro por UID, depois por email se não encontrar
        let perfil = null;
        let userRole = null;

        // 1. Tentar buscar por UID na coleção usuarios
        try {
          const docSnap = await getDoc(doc(db, 'usuarios', fbUser.uid));
          if (docSnap.exists()) {
            perfil = docSnap.data();
            userRole = perfil.role;
          }
        } catch (_) {
          // Continua para próxima tentativa
        }

        // 2. Se não encontrou por UID, buscar por email na coleção usuarios
        if (!perfil) {
          try {
            const usuariosQuery = query(
              collection(db, 'usuarios'),
              where('email', '==', fbUser.email),
              limit(1)
            );
            const usuariosSnap = await getDocs(usuariosQuery);
            if (!usuariosSnap.empty) {
              perfil = usuariosSnap.docs[0].data();
              userRole = perfil.role;
            }
          } catch (_) {
            // Continua sem perfil
          }
        }

        // 3. Se ainda não encontrou, buscar por UID field na coleção usuarios
        if (!perfil) {
          try {
            const usuariosQuery = query(
              collection(db, 'usuarios'),
              where('uid', '==', fbUser.uid),
              limit(1)
            );
            const usuariosSnap = await getDocs(usuariosQuery);
            if (!usuariosSnap.empty) {
              perfil = usuariosSnap.docs[0].data();
              userRole = perfil.role;
            }
          } catch (_) {
            // Continua sem perfil
          }
        }

        // 4. Verificar na coleção admins (estrutura antiga) se não encontrou role
        if (!userRole && fbUser.email) {
          try {
            const adminQuery = query(
              collection(db, 'admins'),
              where('email', '==', fbUser.email),
              limit(1)
            );
            const adminSnap = await getDocs(adminQuery);
            if (!adminSnap.empty && adminSnap.docs[0].data()?.active !== false) {
              userRole = 'admin';
            }
          } catch (_) {
            // Continua sem role
          }
        }

        const hydratedUser = {
          uid: fbUser.uid,
          email: fbUser.email || perfil?.email || '',
          name: perfil?.name || perfil?.nome || fbUser.displayName || fbUser.email || 'Usuário',
          plano: perfil?.plano || perfil?.plan?.name || perfil?.plan || 'Free',
          role: userRole,
          blocked: perfil?.blocked === true,
          active: perfil?.active !== undefined ? perfil.active : true,
          planExpirationDate: perfil?.planExpirationDate || perfil?.subscription?.expirationDate || null
        };

        setUser((prevUser) => {
          const sameEmail = prevUser?.email && prevUser.email === hydratedUser.email;
          return {
            ...hydratedUser,
            role: hydratedUser.role || (sameEmail ? prevUser?.role : null)
          };
        });

        // Carregar favoritas do usuário
        try {
          const favDoc = await getDoc(doc(db, 'user_favorites', fbUser.uid));
          if (favDoc.exists()) {
            setUserFavorites(favDoc.data().subcategorias || []);
          } else {
            setUserFavorites([]);
          }
        } catch (_) {
          setUserFavorites([]);
        }

        // Checar se é admin ou especialista
        if (hydratedUser.email) {
          try {
            let hasAccess = false;

            // 1. Verificar na coleção "admins" (estrutura antiga)
            const adminQuery = query(
              collection(db, 'admins'),
              where('email', '==', hydratedUser.email),
              limit(1)
            );
            const adminSnap = await getDocs(adminQuery);

            if (!adminSnap.empty && adminSnap.docs[0].data()?.active !== false) {
              hasAccess = true;
            } else {
              // 2. Verificar na coleção "usuarios" se é admin ou especialista
              const userQuery = query(
                collection(db, 'usuarios'),
                where('email', '==', hydratedUser.email),
                limit(1)
              );
              const userSnap = await getDocs(userQuery);

              if (!userSnap.empty) {
                const userData = userSnap.docs[0].data();
                // Verificar se tem role de admin ou especialista e está ativo
                if ((userData.role === 'admin' || userData.role === 'especialista') && userData.active !== false) {
                  hasAccess = true;
                }
              }
            }

            setIsAdminOrSpecialist(hasAccess);
          } catch (_) {
            setIsAdminOrSpecialist(false);
          }
        } else {
          setIsAdminOrSpecialist(false);
        }
      } catch {
        setUser(null);
        setIsAdminOrSpecialist(false);
        setUserFavorites([]);
      }
    });

    return () => unsub();
  }, []);

  // ===========================================
  // 2) Lembrar a última rota (para voltar pós-login)
  // ===========================================
  useEffect(() => {
    const path = location.pathname + location.search + location.hash;
    if (location.pathname !== '/login' && location.pathname !== '/admin-login') {
      sessionStorage.setItem('lastPath', path);
    }
  }, [location.pathname, location.search, location.hash]);

  // Quando logar e estiver na /login, voltar para a última página
  useEffect(() => {
    if (location.pathname === '/login' && user && user.uid) {
      const lastPath = sessionStorage.getItem('lastPath') || '/';
      const t = setTimeout(() => navigate(lastPath, { replace: true }), 50);
      return () => clearTimeout(t);
    }
  }, [user, location.pathname, navigate]);


  // =========================
  // 3) Sistema de Favoritas
  // =========================
  const toggleFavorite = async (subcategoria) => {
    if (!user?.uid) {
      // Se não estiver logado, redirecionar para login
      navigate('/login');
      return;
    }

    try {
      const isFavorite = userFavorites.includes(subcategoria);
      const userFavRef = doc(db, 'user_favorites', user.uid);

      if (isFavorite) {
        // Remover dos favoritos
        await updateDoc(userFavRef, {
          subcategorias: arrayRemove(subcategoria)
        });
        setUserFavorites(prev => prev.filter(fav => fav !== subcategoria));
      } else {
        // Adicionar aos favoritos
        try {
          await updateDoc(userFavRef, {
            subcategorias: arrayUnion(subcategoria),
            updatedAt: new Date()
          });
        } catch (error) {
          // Se o documento não existir, criar
          if (error.code === 'not-found') {
            await setDoc(userFavRef, {
              userId: user.uid,
              userEmail: user.email,
              subcategorias: [subcategoria],
              createdAt: new Date(),
              updatedAt: new Date()
            });
          } else {
            throw error;
          }
        }
        setUserFavorites(prev => [...prev, subcategoria]);
      }
    } catch (error) {
      console.error('Erro ao toggle favorita:', error);
      alert('Erro ao salvar favorita. Tente novamente.');
    }
  };

  // =========================
  // 4) Categorias e Helpers
  // =========================
  const iconMap = {
    FaLayerGroup,
    FaLaptopCode,
    FaCogs,
    FaBriefcase,
    FaGraduationCap,
    FaClipboardCheck
  };

  const getCategoryInfo = () => ({ color: '#209cd3', bgColor: 'rgba(32, 156, 211, 0.05)' });

  // Debug: vamos ver o que está acontecendo
  if (user) {
    console.log('User data:', user);
    console.log('User role:', user?.role);
    console.log('User plano:', user?.plano);
  }

  const effectivePlan = getEffectivePlan(user);

  // Especialistas têm plano "Especialista" e admins são sempre tratados como Premium
  const shouldShowBanner = () => {
    return effectivePlan.shouldShowPremiumOffer;
  };

  const getEffectivePlano = () => {
    return effectivePlan.plano;
  };

  const shouldShowPremiumButton = () => {
    return effectivePlan.shouldShowPremiumOffer;
  };

  const getIconForSubcategory = (subcategoryName) => {
    const name = subcategoryName.toLowerCase();
    if (name.includes('algoritmo') || name.includes('programação') || name.includes('código')) return FaCode;
    if (name.includes('cálculo') || name.includes('matemática') || name.includes('estatística')) return FaCalculator;
    if (name.includes('banco') || name.includes('dados') || name.includes('sql')) return FaDatabase;
    if (name.includes('web') || name.includes('interface') || name.includes('front')) return FaGlobe;
    if (name.includes('sistema') || name.includes('infraestrutura') || name.includes('servidor')) return FaServer;
    if (name.includes('química') || name.includes('física') || name.includes('ciência')) return FaFlask;
    if (name.includes('administração') || name.includes('gestão') || name.includes('negócio')) return FaUsers;
    if (name.includes('desenvolvimento') || name.includes('ferramenta') || name.includes('software')) return FaTools;
    if (name.includes('estatística') || name.includes('probabilidade') || name.includes('gráfico')) return FaChartBar;
    if (name.includes('inteligência') || name.includes('machine') || name.includes('learning')) return FaBrain;
    if (name.includes('robótica') || name.includes('automação') || name.includes('robot')) return FaRobot;
    if (name.includes('prova') || name.includes('avaliação') || name.includes('teste')) return FaPencilAlt;
    if (name.includes('natureza') || name.includes('biologia') || name.includes('átomo')) return FaAtom;
    if (name.includes('linguagem') || name.includes('comunicação') || name.includes('português')) return FaLanguage;
    if (name.includes('engenharia') || name.includes('produção') || name.includes('industrial')) return FaIndustry;
    if (name.includes('planejamento') || name.includes('organização') || name.includes('estratégia')) return FaClipboardList;
    if (name.includes('pesquisa') || name.includes('laboratório') || name.includes('experimento')) return FaMicroscope;
    if (name.includes('educação') || name.includes('pedagogia') || name.includes('ensino')) return FaUserGraduate;
    return FaBook;
  };

  useEffect(() => {
    const fetchCategories = async () => {
      const ref = collection(db, 'categories');
      const snapshot = await getDocs(ref);
      const categoriesData = {};
      snapshot.forEach(docSnap => {
        const data = docSnap.data();
        categoriesData[data.name] = {
          icon: iconMap[data.icon] || FaLayerGroup,
          subcategories: data.subcategories.map(sub => sub.name)
        };
        return null;
      });
      setCategories(categoriesData);
    };
    fetchCategories();
  }, []);

  // =========================
  // 5) Helpers UI
  // =========================
  const closeModal = () => {
    setActiveCategory(null);
    setSearchTerm('');
  };
  const handleSearchChange = (e) => setSearchTerm(e.target.value);
  const handleMainSearchChange = (e) => setMainSearchTerm(e.target.value);

  const handleBuscaGlobal = (e) => {
    e.preventDefault();
    if (mainSearchTerm.trim()) {
      navigate(`/busca?q=${encodeURIComponent(mainSearchTerm.trim())}`);
    }
  };

  const normalizedSearchTerm = normalizeText(searchTerm);
  const normalizedMainSearchTerm = normalizeText(mainSearchTerm);

  const filteredSubcategories = activeCategory
    ? categories[activeCategory].subcategories.filter(sub =>
      normalizeText(sub).includes(normalizedSearchTerm)
    )
    : [];

  const filteredCategories = Object.entries(categories).filter(([category]) =>
    normalizeText(category).includes(normalizedMainSearchTerm)
  );

  // Fechar sidebar quando clicar no overlay (mobile)
  const closeSidebar = () => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  return (
    <UserContext.Provider value={{
      user,
      setUser,
      userFavorites,
      setUserFavorites,
      toggleFavorite
    }}>
      <div className="container">
        {/* Overlay para mobile */}
        {isMobile && sidebarOpen && (
          <div className="mobile-overlay" onClick={closeSidebar}></div>
        )}

        <aside className={`sidebar ${isMobile ? (sidebarOpen ? 'sidebar-open' : 'sidebar-closed') : ''}`}>
          {/* Logo apenas no desktop */}
          {!isMobile && (
            <div className="logo-container">
              <img src={logo} alt="Logo EAD Conect" className="logo" />
            </div>
          )}

          <nav className="menu-section">
            <div className="menu-section-title">Menu Principal</div>
            <ul>
              <li
                onClick={() => navigate('/')}
                className={`flex items-center gap-2 p-2 cursor-pointer ${location.pathname === '/' ? 'active' : ''}`}
              >
                <FaHome className="icon" /> Início
              </li>
              <li
                onClick={() => navigate('/quem-somos')}
                className={`flex items-center gap-2 p-2 cursor-pointer ${location.pathname === '/quem-somos' ? 'active' : ''}`}
              >
                <FaInfoCircle className="icon" /> Quem Somos
              </li>
              <li
                onClick={() => navigate('/perguntas-frequentes')}
                className={`flex items-center gap-2 p-2 cursor-pointer ${location.pathname === '/perguntas-frequentes' ? 'active' : ''}`}
              >
                <FaQuestionCircle className="icon" /> Perguntas Frequentes
              </li>

              {/* Acessar Admin (para admins E especialistas) */}
              {isAdminOrSpecialist && (
                <li
                  onClick={() => navigate('/admin-panel')}
                  className={`flex items-center gap-2 p-2 cursor-pointer ${location.pathname === '/admin-panel' ? 'active' : ''}`}
                >
                  <FaUserShield className="icon" />
                  {user?.role === 'especialista' ? 'Painel Especialista' : 'Acessar Admin'}
                </li>
              )}
            </ul>
          </nav>

          <nav className="menu-section">
            <div className="menu-section-title">Categorias</div>
            <ul>
              {filteredCategories.length > 0 ? (
                filteredCategories.map(([category, { icon: Icon }]) => (
                  <li
                    key={category}
                    onClick={() => setActiveCategory(category === activeCategory ? null : category)}
                    className={`flex items-center gap-2 p-2 cursor-pointer ${activeCategory === category ? 'active' : ''}`}
                  >
                    <Icon className="icon" />
                    {category}
                  </li>
                ))
              ) : (
                <p className="sidebar-no-results">Nenhuma categoria encontrada.</p>
              )}
            </ul>
          </nav>

          <div className="menu-actions">
            {!user && (
              <>
                <button className="login-menu-button" onClick={() => navigate('/login')}>
                  <FaSignInAlt /> Login
                </button>
                <button className="teste-gratuito-button" onClick={() => navigate('/cadastro')}>
                  <FaGift /> Teste Gratuito
                </button>
              </>
            )}
            {shouldShowPremiumButton() && (
              <button className="premium-menu-button" onClick={() => navigate('/premium')}>
                <FaStar /> Torne-se Premium
              </button>
            )}
          </div>
        </aside>

        {/* Modal de subcategorias COM SISTEMA DE FAVORITAS */}
        {activeCategory && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="subcategory-modal-content" onClick={(e) => e.stopPropagation()}>
              <button className="modal-close" onClick={closeModal}>
                <FaTimes />
              </button>

              <div className="modal-header">
                <h2 className="modal-title">{activeCategory}</h2>
                <div className="modal-buttons">
                  <button
                    className="ver-todas-button"
                    onClick={() => {
                      navigate(`/materiais-categoria/${encodeURIComponent(activeCategory)}`);
                      closeModal();
                    }}
                  >
                    Ver todos os materiais
                  </button>
                  <button
                    className="ver-subcategorias-button"
                    onClick={() => {
                      navigate(`/categoria/${encodeURIComponent(activeCategory)}`);
                      closeModal();
                    }}
                  >
                    Ver todas subcategorias
                  </button>
                </div>
              </div>

              <div className="modal-search">
                <input
                  type="text"
                  placeholder="Buscar subcategorias..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="modal-search-input"
                />
                <FaSearch className="modal-search-icon" />
              </div>

              <div className="subcategory-cards-grid">
                {filteredSubcategories.length > 0 ? (
                  filteredSubcategories.map((sub, index) => {
                    const { color, bgColor } = getCategoryInfo();
                    const IconComponent = getIconForSubcategory(sub);
                    const isFavorite = userFavorites.includes(sub);

                    return (
                      <div
                        key={index}
                        className="subcategory-card"
                        style={{
                          '--card-color': color,
                          '--card-bg': bgColor
                        }}
                      >
                        {/* Botão de favorita */}
                        <button
                          className={`favorite-button ${isFavorite ? 'favorited' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFavorite(sub);
                          }}
                          title={isFavorite ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
                        >
                          {isFavorite ? <FaHeart /> : <FaRegHeart />}
                        </button>

                        {/* Conteúdo do card */}
                        <div
                          className="card-content"
                          onClick={() => {
                            navigate(`/materiais/${encodeURIComponent(sub)}`);
                            closeModal();
                          }}
                        >
                          <div className="card-icon">
                            <IconComponent />
                          </div>
                          <div className="card-title">{sub}</div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="no-results">
                    <div className="no-results-icon">
                      <FaSearch />
                    </div>
                    <p>Nenhuma subcategoria encontrada</p>
                    <small>Tente buscar com outras palavras</small>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <main className={`main-content ${isMobile ? 'main-content-mobile' : ''}`}>
          <header className="topbar">
            {/* Menu hamburger e logo para mobile */}
            {isMobile && (
              <>
                <button
                  className="hamburger-button"
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  aria-label="Toggle menu"
                >
                  <FaBars />
                </button>
                <div className="mobile-logo">
                  <img src={logo} alt="Logo EAD Conect" />
                </div>
              </>
            )}

            <div className="topbar-actions">
              {user ? (
                <>
                  {!shouldShowPremiumButton() ? (
                    <span className="plan-name">
                      Plano: {getEffectivePlano()}
                    </span>
                  ) : (
                    <button className="premium-button" onClick={() => navigate('/premium')}>
                      <FaStar /> {isMobile ? '' : 'Torne-se Premium'}
                    </button>
                  )}
                  <UserMenu />
                </>
              ) : (
                <>
                  <button className="premium-button" onClick={() => navigate('/premium')}>
                    <FaStar /> {isMobile ? '' : 'Torne-se Premium'}
                  </button>
                  <button className="login-button" onClick={() => navigate('/login')}>
                    <FaSignInAlt /> {isMobile ? '' : 'Entrar'}
                  </button>
                </>
              )}
            </div>
          </header>

          <Routes>
            <Route path="/" element={
              <section className="content">
                <h1 className="content-title">O que você está estudando hoje?</h1>

                <form onSubmit={handleBuscaGlobal} className="search-box">
                  <input
                    type="text"
                    placeholder="Busque matérias, temas ou professores..."
                    className="search-input"
                    value={mainSearchTerm}
                    onChange={handleMainSearchChange}
                  />
                  <button type="submit" className="search-button">
                    <FaSearch />
                  </button>
                </form>

                <div className="content-text">
                  <p>
                    Aqui você poderá encontrar materiais de estudo, interagir com professores
                    e compartilhar conhecimento. Tudo 100% online.
                  </p>
                </div>

                {shouldShowBanner() && (
                  <div className="banner-container">
                    <div className="banner">
                      <img src={pessoa} alt="Pessoa" className="banner-image" />
                      <div className="banner-text">
                        <p>Conquiste o seu futuro com a EAD Conect!<br />
                          Na EAD Conect, acreditamos que o seu futuro se constrói em rede. Aqui você encontra milhares de conteúdos organizados, aulas dinâmicas e exercícios práticos e ainda se torna parte de uma verdadeira comunidade de conhecimento. Conecte-se com outros alunos, crie grupos de estudo e compartilhe materiais. Junte-se a nós e construa seu futuro no poder da conexão!</p>
                        <button className="banner-button" onClick={() => navigate('/premium')}>Seja Premium</button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="banners-categorias-container">
                  <h2 className="banners-titulo">Principais Categorias</h2>
                  <div className="banners-grid">
                    {[
                      { nome: "Engenharia de Produção", imagem: banner1, route: "categoria" },
                      { nome: "Ciência de Dados", imagem: banner3, route: "categoria" },
                      { nome: "Tecnologia da Informação", imagem: banner2, route: "categoria" },
                      { nome: "Pedagogia", imagem: banner4, route: "categoria" },
                      { nome: "Administração", imagem: banner5, route: "categoria" },
                      { nome: "Engenharia de Computação", imagem: banner7, route: "categoria" },
                    ].map((cat, index) => {
                      const basePath = cat.route === "categoria" ? "/categoria/" : "/materiais-categoria/";
                      const targetPath = `${basePath}${encodeURIComponent(cat.nome)}`;

                      return (
                        <div
                          key={index}
                          className="banner-card"
                          onClick={() => navigate(targetPath)}
                        >
                          <img src={cat.imagem} alt={cat.nome} />
                          <div className="text-center">{cat.nome}</div>
                        </div>
                      )
                    })}
                  </div>
                </div>

                {/* Seção de Depoimentos */}
                <div className="depoimentos-section">
                  <Depoimentos />
                </div>

                {/* Popup de Avisos - Sistema de Notificações */}
                <PopupAvisos />
              </section>
            } />

            {/* Rotas Existentes */}
            <Route path="/premium" element={<PremiumPlans />} />
            <Route path="/login" element={<Login />} />
            <Route path="/cadastro" element={<Cadastro />} />
            <Route path="/quem-somos" element={<QuemSomos />} />
            <Route path="/perguntas-frequentes" element={<PerguntasFrequentes />} />
            <Route path="/perfil" element={<Perfil />} />

            {/* ROTA - Meus Favoritos */}
            <Route path="/meus-favoritos" element={<MeusFavoritos />} />

            {/* Rotas Admin Existentes */}
            <Route path="/admin-login" element={<AdminLogin />} />
            <Route path="/admin-panel" element={
              <RequireAdminOrSpecialist>
                <AdminPanel />
              </RequireAdminOrSpecialist>
            } />
            <Route path="/admin/administradores" element={
              <RequireAdminOrSpecialist>
                <GerenciarAdministradores />
              </RequireAdminOrSpecialist>
            } />
            <Route path="/admin/categorias" element={
              <RequireAdminOrSpecialist>
                <GerenciarCategorias />
              </RequireAdminOrSpecialist>
            } />
            <Route path="/admin/materiais" element={
              <RequireAdminOrSpecialist>
                <GerenciarMateriais />
              </RequireAdminOrSpecialist>
            } />
            <Route path="/admin/materiais-aprovacao" element={
              <RequireAdminOrSpecialist>
                <MateriaisAprovacao />
              </RequireAdminOrSpecialist>
            } />
            <Route path="/admin/usuarios" element={
              <RequireAdminOrSpecialist>
                <GerenciarUsuarios />
              </RequireAdminOrSpecialist>
            } />
            <Route path="/admin/planos" element={
              <RequireAdminOrSpecialist>
                <GerenciarPlanos />
              </RequireAdminOrSpecialist>
            } />

            {/* ROTA - Perguntas Admin */}
            <Route path="/admin/perguntas-alunos" element={
              <RequireAdminOrSpecialist>
                <PerguntasAdmin />
              </RequireAdminOrSpecialist>
            } />

            {/* NOVA ROTA - Gerenciar Avisos */}
            <Route path="/admin/avisos" element={
              <RequireAdminOrSpecialist>
                <GerenciarAvisos />
              </RequireAdminOrSpecialist>
            } />

            {/* Rotas de Conteúdo */}
            <Route path="/conteudo/:id" element={<VerConteudo />} />
            <Route path="/busca" element={<ResultadosBusca />} />
            <Route path="/materiais-categoria/:nome" element={<MateriaisPorCategoria />} />
            <Route path="/materiais/:nomeDaSubcategoria" element={<MateriaisPorSubcategoria />} />

            {/* ROTA - Categoria Detalhada */}
            <Route path="/categoria/:nome" element={<CategoriaDetalhada />} />

            {/* ROTA - Pergunta do Aluno */}
            <Route path="/pergunta-aluno" element={<PerguntaAluno />} />
          </Routes>
        </main>
      </div>
    </UserContext.Provider>
  );
}

export default function WrappedApp() {
  return (
    <Router>
      <App />
    </Router>
  );
}
